/**
 * Turning an API error into something a customer can act on.
 *
 * WHY THIS EXISTS. Tom's signup failed six times on 2026-09-16 and he was never told
 * why. The server said "Ensure that there are no more than 6 digits in total" about
 * daily_consumption_lbs - a field that is COMPUTED and has no input on the form - so the
 * animal form set its banner to "Please fix the errors below" and there was nothing
 * below. Two of the other onboarding steps were worse: one rendered a DRF error object
 * as "[object Object]", and the property step threw the server's reason away entirely in
 * four bare catch blocks.
 *
 * The rule this encodes: a customer should always know WHICH field and WHAT TO DO. An
 * error about a field they cannot see has to be said in words, in the banner, in
 * language that names something they recognise.
 */

/** API field name -> what a customer calls it. */
export const FIELD_LABELS: Record<string, string> = {
  // Animals
  daily_consumption_lbs: 'daily feed amount',
  daily_consumption_cups: 'daily feed amount',
  recommended_consumption_lbs: 'recommended feed amount',
  count: 'how many animals',
  animal_type: 'animal type',
  animal_subtype: 'breed type',
  life_stage: 'life stage',
  weight_lbs: 'weight',
  target_weight_lbs: 'target weight',
  acquired_weight_lbs: 'weight when you got them',
  birth_date: 'birth date',
  acquired_date: 'the date you got them',
  acquired_age_months: 'age when you got them',
  name: 'name',
  breed: 'breed',
  // Bins
  bin_ids: 'which bins to keep filled',
  feed_product: 'feed',
  feed_product_id: 'feed',
  capacity_lbs: 'bin size',
  current_lbs: 'how much feed is in the bin',
  // Preferences
  preferred_day: 'delivery day',
  service_tier: 'service level',
  // Property + address
  service_address: 'street address',
  city: 'city',
  state: 'state',
  postal_code: 'ZIP code',
  zip_code: 'ZIP code',
  gate_code: 'gate code',
  access_notes: 'access notes',
  dog_notes: 'notes about your dogs',
  image: 'photo',
  // Account
  email: 'email address',
  phone: 'phone number',
  password: 'password',
};

/** Fall back to the field name in plain words: acquired_age_months -> acquired age months. */
export const labelFor = (field: string): string =>
  FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\bid\b/gi, '').trim();

// Keys that carry a message about the request as a whole rather than one field.
const GENERAL_KEYS = ['detail', 'error', 'message', 'non_field_errors', 'errors'];

const flatten = (value: any): string[] => {
  if (value == null) return [];
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(flatten);
  if (typeof value === 'object') return Object.values(value).flatMap(flatten);
  return [String(value)];
};

export interface ParsedApiError {
  /** Per-field messages, keyed by API field name, for fields the form shows. */
  fieldErrors: Record<string, string>;
  /** Messages about fields the form does NOT show, already written in words. */
  leftovers: string[];
  /** What to put in the banner. Never empty. */
  message: string;
}

/**
 * @param err      whatever was thrown: an axios error, a fetch-handler object, an
 *                 ApiValidationError, or a plain payload.
 * @param shownFields  fields this form actually renders an error next to. Anything else
 *                 becomes a leftover and is spoken aloud in the banner.
 * @param fallback what to say when the server gave us nothing usable.
 */
export const parseApiError = (
  err: any,
  { shownFields = [], fallback = 'Something went wrong. Please try again.' }:
    { shownFields?: string[]; fallback?: string } = {},
): ParsedApiError => {
  const payload =
    (err && err.fieldErrors && Object.keys(err.fieldErrors).length ? err.fieldErrors : null)
    ?? err?.response?.data
    ?? err?.data
    ?? (typeof err === 'object' ? err : null);

  const fieldErrors: Record<string, string> = {};
  const general: string[] = [];

  if (typeof payload === 'string') {
    general.push(payload);
  } else if (payload && typeof payload === 'object') {
    Object.entries(payload).forEach(([key, value]) => {
      const messages = flatten(value);
      if (!messages.length) return;
      if (GENERAL_KEYS.includes(key)) {
        general.push(...messages);
      } else {
        fieldErrors[key] = messages.join(' ');
      }
    });
  }

  const shown = new Set(shownFields);
  const leftovers = Object.entries(fieldErrors)
    .filter(([field]) => !shown.has(field))
    .map(([field, message]) => {
      const label = labelFor(field);
      // Capitalise the label, keep the server's sentence after it.
      return `${label.charAt(0).toUpperCase()}${label.slice(1)}: ${message}`;
    });

  const visible = Object.keys(fieldErrors).filter((field) => shown.has(field));

  let message: string;
  if (general.length) {
    message = general.join(' ');
  } else if (leftovers.length) {
    message = leftovers.join(' ');
  } else if (visible.length) {
    message = 'Please fix the highlighted fields below.';
  } else {
    message = err?.message && typeof err.message === 'string' ? err.message : fallback;
  }

  return { fieldErrors, leftovers, message };
};
